"use client";

import { useLocale, useTranslations } from "next-intl";
import { BookOpen, Lock } from "lucide-react";
import { formatClockTime } from "@/features/contests/lib/format";
import { useContestDisplayZone } from "@/features/contests/lib/display-zone";
import { Button } from "@/components/ui/button";
import { RichContent } from "@/components/rich-content";
import { Skeleton } from "@/components/ui/skeleton";
import { useJudgeProblemEditorial } from "../../hooks";

/**
 * The editorial tab, `EX-EDT01` (ADR-0085).
 *
 * Open to everyone, a stranger included, and behind one click: the tab opens
 * on "try it yourself first", and the text is fetched only when the reader
 * asks for it, so a reader who only glanced at the tab has not been handed the
 * solution. The server withholds it while a round that has not ended mounts the
 * problem, and the tab says so rather than showing an empty page.
 *
 * The language follows the statement's toggle; a Bangla reader of an editorial
 * written only in English gets the English with one line saying so.
 */
export function EditorialTab({
  slug,
  lang,
  revealed,
  onReveal,
}: {
  slug: string;
  lang: "en" | "bn";
  revealed: boolean;
  onReveal: () => void;
}) {
  const t = useTranslations("judge.problem");
  const locale = useLocale();
  const zone = useContestDisplayZone();
  const query = useJudgeProblemEditorial(slug, revealed);

  if (!revealed) {
    return (
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
        <div className="flex items-start gap-3">
          <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-neutral-500" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-neutral-900">{t("editorialTryFirstTitle")}</p>
            <p className="mt-1 text-sm leading-relaxed text-neutral-600">{t("editorialTryFirstBody")}</p>
            <Button type="button" variant="secondary" className="mt-4" onClick={onReveal}>
              {t("editorialShow")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div className="space-y-3" aria-busy="true">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    );
  }

  if (query.isError) {
    return <p className="text-sm text-red-700">{t("editorialLoadFailed")}</p>;
  }

  const data = query.data;
  if (data.withheld === "live_contest") {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <p className="leading-relaxed">{t("editorialWithheld")}</p>
      </div>
    );
  }

  // `EX-VRT01`: the reader's own virtual try with this problem runs.
  if (data.withheld === "virtual_round") {
    const round = data.virtualRound;
    return (
      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <p className="leading-relaxed">
          {round
            ? t("editorialVirtual", { title: round.title_en, time: formatClockTime(round.opensAt, locale, zone) })
            : t("editorialWithheld")}
        </p>
      </div>
    );
  }

  const editorial = data.editorial;
  const bn = lang === "bn" ? editorial?.editorial_bn : null;
  const html = bn ?? editorial?.editorial_en ?? editorial?.editorial_bn ?? null;
  if (!html) {
    return <p className="text-sm text-neutral-500">{t("editorialNone")}</p>;
  }
  const englishForBangla = lang === "bn" && !bn && !!editorial?.editorial_en;

  return (
    <article>
      {englishForBangla ? <p className="mb-3 text-xs text-neutral-500">{t("editorialInEnglish")}</p> : null}
      <div className="text-sm leading-relaxed text-neutral-800">
        <RichContent html={html} />
      </div>
    </article>
  );
}
